import { create } from 'zustand';
import {
  KpiTitle,
  Salon,
  SalonActionPlan,
  SalonInputData,
  SalonKpiData,
  SalonKpiPlannedData,
  SalonListItem,
  SendActionPlanData,
  SendCompletedPlanData,
  kpiTitleToDbKey,
} from '../types';
import {
  CyHistoricalDataValues,
  SalonHistoricalData,
  SalonHistoricalKPI,
} from '../types/SalonHistoricalData';
import { ShowMessageCalBack, IFieldValues } from '../components';
import { fetchData } from '../axios/fetchData';
import { NavigateFunction } from 'react-router';
import { normalizeYearQuarter } from '../utils/normalizeYearQuarter';

type SalonState = {
  salon: Salon;
  salonList: SalonListItem[];
  salonActionPlan: SalonActionPlan[];
  salonInputData: SalonInputData;
  salonHistoricalData: CyHistoricalDataValues;
  salonHistoricalKPI: SalonHistoricalKPI;
  actionPlan: SalonActionPlan;
  salonKpiData: SalonKpiData;
  salonKpiPlannedData: SalonKpiPlannedData;
  salonSelectedKpi: string[];
  isLoading: boolean;
};

type SalonActions = {
  fetchSalonList: (
    userId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonActionPlanList: (
    salonId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonInputData: (
    salonId: string,
    year: string,
    quarter: number,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonHistoricalData: (
    salonId: string,
    year: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonHistoricalKPI: (
    salonId: string,
    year: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonKpiData: (
    salonId: string,
    planId: string,
    year: string,
    quarter: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  fetchSalonKpiPlannedData: ({
    salonId,
    year,
    quarter,
    showMessage,
    navigate,
  }: {
    salonId: string;
    year: string;
    quarter: string;
    showMessage: ShowMessageCalBack;
    navigate: NavigateFunction;
  }) => void;
  updateSalonKpiPlannedData: ({
    salonId,
    year,
    quarter,
    showMessage,
    navigate,
    callBack,
    kpis,
  }: {
    salonId: string;
    year: string;
    quarter: string;
    showMessage: ShowMessageCalBack;
    navigate: NavigateFunction;
    kpis: SalonKpiPlannedData['kpi'];
    callBack?: () => void;
  }) => void;
  updateNotes: (
    salonId: string,
    draftText: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  updateSalonInputData: (
    salonId: string,
    period: [string, number],
    data: IFieldValues,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
    callBack: (data: SalonInputData) => void,
  ) => void;
  updateSalonHistoricalData: (
    salonId: string,
    year: string,
    isAnnualMode: boolean,
    data: Record<string, unknown>,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
    callBack?: () => void,
  ) => void;
  updateSelectedKpi: (newKpi: string[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  fetchSalon: (
    salonId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  resetSalon: () => void;
  getActionPlan: (
    planId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => void;
  updateActionPlan: (
    data: SalonActionPlan,
    planId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => Promise<SalonActionPlan>;
  sendActionPlan: (
    data: SendActionPlanData,
    planId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => Promise<SalonActionPlan>;
  sendCompletedPlan: (
    data: SendCompletedPlanData,
    planId: string,
    showMessage: ShowMessageCalBack,
    navigate: NavigateFunction,
  ) => Promise<SalonActionPlan>;
  resetStore: () => void;
  getDbKeyForKpiTitle: (title: KpiTitle) => KpiTitle;
};

type PlannedSalonKpiPayload = { [field: string]: number | string };

export const useSalonStore = create<SalonState & SalonActions>()((set, get) => ({
  salon: null,
  salonList: [],
  salonActionPlan: [],
  salonInputData: null,
  salonHistoricalData: null,
  salonHistoricalKPI: null,
  salonKpiData: null,
  salonKpiPlannedData: null,
  salonSelectedKpi: [],
  isLoading: false,
  actionPlan: null,
  setIsLoading: (isLoading) => {
    return set({ isLoading });
  },
  updateNotes: async (salonId, draftText, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: import.meta.env.VITE_APP_SALON_DETAIL + salonId,
        method: 'PATCH',
        data: { notes: draftText },
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);
    return set((prevState) => ({
      ...prevState,
      salon: {
        ...prevState.salon,
        notes: (result?.data as Salon).notes || '',
      },
    }));
  },
  fetchSalon: async (salonId: string, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: import.meta.env.VITE_APP_SALON_DETAIL + salonId,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salon: (result?.data as Salon) || null,
    });
  },
  resetSalon: () => {
    return set({
      salon: null,
    });
  },
  fetchSalonList: async (userId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: import.meta.env.VITE_APP_SALON_LIST + userId,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);
    return set({
      salonList: (Object.values(result?.data) as SalonListItem[]) || [],
    });
  },
  fetchSalonActionPlanList: async (salonId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: import.meta.env.VITE_APP_ACTION_PLAN_LIST + salonId,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonActionPlan: (Object.values(result?.data) as SalonActionPlan[]) || [],
    });
  },
  fetchSalonInputData: async (salonId, year, quarter, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_INPUT_DATA}${salonId}/${year}/${quarter}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonInputData: (result?.data as SalonInputData) || null,
    });
  },
  fetchSalonKpiData: async (salonId, planId, year, quarter, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_SALON_KPI}${salonId}/${planId}/${year}/${quarter}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonKpiData: (result?.data as SalonKpiData) || null,
    });
  },
  fetchSalonKpiPlannedData: async ({ salonId, year, quarter, showMessage, navigate }) => {
    get().setIsLoading(true);
    const { year: normalizedYear, quarter: normalizedQuarter } = normalizeYearQuarter({
      year: year,
      quarter: quarter,
      shouldShift: true,
    });

    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_SALON_KPI_PLANNED}${salonId}/${normalizedYear}/${normalizedQuarter}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonKpiPlannedData: (result?.data as SalonKpiPlannedData) || null,
    });
  },
  updateSalonKpiPlannedData: async ({
    salonId,
    year,
    quarter,
    showMessage,
    navigate,
    callBack,
    kpis,
  }) => {
    get().setIsLoading(true);
    const salonKpiPlannedData = get().salonKpiPlannedData;
    const payload: PlannedSalonKpiPayload = Object.entries(kpis).reduce((acc, [key, kpi]) => {
      if (kpi.value === null) {
        acc[key] = null;

        return acc;
      }

      const num = Number(kpi.value);
      acc[key] = !isNaN(num) ? num : kpi.value;

      return acc;
    }, {} as PlannedSalonKpiPayload);

    const { year: normalizedYear, quarter: normalizedQuarter } = normalizeYearQuarter({
      year: year,
      quarter: quarter,
      shouldShift: true,
    });

    let method;
    let path;

    if (salonKpiPlannedData?.id) {
      method = 'PATCH';
      path = `${salonId}/${normalizedYear}/${normalizedQuarter}`;
    } else {
      method = 'POST';
      payload.salonDetailPage = Number(salonId);
      payload.year = normalizedYear;
      payload.quarter = normalizedQuarter;
    }

    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_SALON_KPI_PLANNED}${path || ''}`,
        method,
        data: payload,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
      true,
    );
    get().setIsLoading(false);

    if (result?.data && callBack) {
      callBack();
    }
    return set((prevState) => ({
      ...prevState,
      salonKpiPlannedData: (result?.data as SalonKpiPlannedData) || null,
    }));
  },
  getDbKeyForKpiTitle: (title: string): KpiTitle => {
    return (kpiTitleToDbKey as Record<string, KpiTitle>)[title] || (title as KpiTitle);
  },
  updateSelectedKpi: (newKpi: string[]) =>
    set({
      salonSelectedKpi: newKpi,
    }),
  updateSalonInputData: async (salonId, period, data, showMessage, navigate, callBack) => {
    get().setIsLoading(true);
    const inputData = get().salonInputData;
    const year = period[0];
    const quarter = period[1] + 1;
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_INPUT_DATA}${inputData?.id ? `${salonId}/${year}/${quarter}` : ``}`,
        method: inputData?.id ? 'PATCH' : 'POST',
        data: {
          year: year,
          quarter: quarter,
          salonDetailPage: salonId,
          ...data,
        },
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
      true,
    );
    get().setIsLoading(false);

    if (result?.data) {
      callBack(result?.data as SalonInputData);
    }
    return set((prevState) => ({
      ...prevState,
      salonInputData: (result?.data as SalonInputData) || null,
    }));
  },
  fetchSalonHistoricalData: async (salonId, year, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_HISTORICAL_DATA}${salonId}/${year}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonHistoricalData: (result?.data as SalonHistoricalData).cyHistoricalData || null,
    });
  },
  fetchSalonHistoricalKPI: async (salonId, year, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_HISTORICAL_DATA_KPI}${salonId}/${year}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      salonHistoricalKPI: (result?.data as SalonHistoricalData).kpi || null,
    });
  },
  updateSalonHistoricalData: async (
    salonId,
    year,
    isAnnualMode,
    data,
    showMessage,
    navigate,
    callBack,
  ) => {
    get().setIsLoading(true);
    const historicalData = get().salonHistoricalData;
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_HISTORICAL_DATA}${historicalData?.id ? `${salonId}/${year}` : ``}`,
        method: historicalData?.id ? 'PATCH' : 'POST',
        data: {
          salonDetailPage: salonId,
          year: year,
          annualInputMode: isAnnualMode,
          ...data,
        },
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    callBack();
    get().setIsLoading(false);

    return set((prevState) => ({
      ...prevState,
      salonHistoricalData: (result?.data as SalonHistoricalData).cyHistoricalData || null,
    }));
  },
  getActionPlan: async (planId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_ACTION_PLAN}${planId}`,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return set({
      actionPlan: (result?.data as SalonActionPlan) || null,
    });
  },
  updateActionPlan: async (data, planId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_ACTION_PLAN}${planId ? planId : ``}`,
        method: planId ? 'PATCH' : 'POST',
        data: data,
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    set((prevState) => ({
      ...prevState,
      actionPlan: (result?.data as SalonActionPlan) || null,
    }));

    return result?.data as SalonActionPlan;
  },
  sendActionPlan: async (data, planId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_SEND_ACTION_PLAN}${planId}`,
        method: 'PATCH',
        data: {
          ...(data as SendActionPlanData),
        },
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return result as SalonActionPlan;
  },
  sendCompletedPlan: async (data, planId, showMessage, navigate) => {
    get().setIsLoading(true);
    const result = await fetchData(
      {
        url: `${import.meta.env.VITE_APP_SEND_COMPLETED_PLAN}${planId}`,
        method: 'PATCH',
        data: {
          ...(data as SendCompletedPlanData),
        },
        params: {
          _format: 'json', // Added format=json to the request
        },
      },
      showMessage,
      navigate,
    );
    get().setIsLoading(false);

    return result as SalonActionPlan;
  },
  resetStore: async () => {
    return set((prevState) => ({
      ...prevState,
      actionPlan: null,
      salonInputData: null,
      salonHistoricalData: null,
      salonHistoricalKPI: null,
      salonSelectedKpi: [],
    }));
  },
}));
